import {
  Autocomplete,
  Button,
  Card,
  Fade,
  Grid,
  InputAdornment,
  Modal,
  Switch,
} from "@mui/material";
import { makeStyles } from "@mui/styles";
import CustomTextField from "components/Form/CustomTextField";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import { useAppContextController } from "context/AppContext";
import useDropdowns from "hooks/useDropdowns";
import useYupValidationResolver from "hooks/useYupValidationResolver";
import { useEffect, useMemo, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import deductionsSchema from "./deductionsSchema";

const useStyle = makeStyles({
  container: {
    position: "absolute",
    width: "25%",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
  },
});

const customValueCode = "%CUSTOM_VALUE%";

const deductionAmountTypeOptions = [
  {
    label: "Flat Amount",
    value: "flat",
  },
  {
    label: "Percentage",
    value: "percentage",
  },
];

const AddDeductionsModal = ({ open, setOpen, onAddDeduction, row }) => {
  const classes = useStyle();

  const { dropdowns: deductionTypes } = useDropdowns({ entity: "payrollDeductionTypes" });

  const deductionTypesOptions = useMemo(() => {
    const result = (deductionTypes ?? []).map((type) => ({
      label: type.name,
      value: type.code,
      amount: type.amount,
    }));
    result.push({
      label: "Custom",
      value: customValueCode,
      amount: "",
    });
    return result;
  }, [deductionTypes]);

  const resolver = useYupValidationResolver(deductionsSchema);

  const paramDeductionOption = useMemo(() => {
    if (row?.rosterData?.deductionCode && deductionTypesOptions?.length) {
      const found = deductionTypesOptions.find((op) => op.value === row?.rosterData?.deductionCode);
      if (found) {
        return found;
      }
      return deductionTypesOptions.find((op) => op.value === customValueCode);
    }
    return "";
  }, [deductionTypesOptions, row?.rosterData?.deductionCode]);

  const {
    control,
    handleSubmit,
    setValue,
    reset,
    watch,
    trigger,
    getValues,
    formState: { dirtyFields, errors },
  } = useForm({
    resolver,
    defaultValues: {
      option: paramDeductionOption ?? "",
      amount: row?.rosterData?.deductionAmount ?? "",
      code: row?.rosterData?.deductionCode ?? "",
      amountType: row?.rosterData?.deductionAmountType ?? "flat",
      // maxAmountType: row?.rosterData?.deductionMaxAmountType ?? "flat",
      // maxAmount: row?.rosterData?.deductionMaxAmount ?? "",
      sponsor: row?.rosterData?.deductionSponsor ?? "No",
    },
  });

  useEffect(() => {
    if (paramDeductionOption) {
      reset({
        option: paramDeductionOption ?? "",
        amount: row?.rosterData?.deductionAmount ?? "",
        code: row?.rosterData?.deductionCode ?? "",
        amountType: row?.rosterData?.deductionAmountType ?? "flat",
        // maxAmountType: row?.rosterData?.deductionMaxAmountType ?? "flat",
        // maxAmount: row?.rosterData?.deductionMaxAmount ?? "",
        sponsor: row?.rosterData?.deductionSponsor ?? "No",
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paramDeductionOption]);

  const handleSubmitFunc = (values) => {
    onAddDeduction(row, values);
    setOpen(false);
  };

  const handleClear = () => {
    onAddDeduction(row, { amount: null, code: null });
    setOpen(false);
  };

  const cleanInput = (value) => {
    // Remove any character that's not a digit or decimal point
    let cleaned = value.replace(/(?!^-)[^\d.]/g, "");

    // Ensure only one decimal point
    const parts = cleaned.split(".");
    if (parts.length > 2) {
      // eslint-disable-next-line prefer-template
      cleaned = parts[0] + "." + parts.slice(1).join("");
    }

    return cleaned;
  };

  return (
    <Modal
      open={open}
      onClose={(event, reason) => {
        setOpen(false);
      }}
      aria-labelledby="modal-modal-title"
      aria-describedby="modal-modal-description"
    >
      <Fade in={open}>
        <Card className={[classes.container].join(" ")}>
          <MDTypography variant="h5" color="dark" sx={{ mt: 2, mb: 1, ml: 2 }} key="text_addNote">
            Deductions
          </MDTypography>
          <MDBox p={3}>
            <form onSubmit={handleSubmit(handleSubmitFunc)}>
              <Grid container xs={12} rowSpacing={3} columnSpacing={2}>
                <Grid item xs={12}>
                  <Controller
                    name="option"
                    control={control}
                    render={({ field, fieldState: { error } }) => (
                      <Autocomplete
                        {...field}
                        options={deductionTypesOptions}
                        autoSelect
                        disableClearable
                        onChange={(e, v) => {
                          if (v?.value === customValueCode) {
                            setValue("code", "");
                            // setValue("amount", "");
                            setValue("option", v);
                            return;
                          }
                          setValue("code", v?.value ?? "");
                          // setValue("amount", v?.amount ?? "");
                          setValue("option", v ?? "");
                        }}
                        sx={{ maxHeight: "44px", width: "100%" }}
                        renderInput={(params) => (
                          <CustomTextField
                            type="text"
                            label="Deduction Type"
                            showError
                            {...params}
                            InputLabelProps={{ shrink: true }}
                            errorMessage={error?.message}
                          />
                        )}
                      />
                    )}
                  />
                </Grid>
                {watch("option")?.value === customValueCode && (
                  <Grid item xs={12}>
                    <Controller
                      name="code"
                      control={control}
                      render={({ field, fieldState: { error } }) => (
                        <CustomTextField
                          {...field}
                          label="Code"
                          InputLabelProps={{ shrink: true }}
                          showError
                          errorMessage={error?.message}
                        />
                      )}
                    />
                  </Grid>
                )}
                <Grid item xs={6}>
                  <Controller
                    name="amount"
                    control={control}
                    render={({ field, fieldState: { error } }) => (
                      <CustomTextField
                        {...field}
                        onChange={(e) => {
                          const val = cleanInput(e.target.value ?? "");
                          setValue("amount", val);
                        }}
                        label="Deduction Amount"
                        InputLabelProps={{ shrink: true }}
                        showError
                        errorMessage={error?.message}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              {watch("amountType") === "percentage" ? "%" : "$"}
                            </InputAdornment>
                          ),
                        }}
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={6}>
                  <Controller
                    name="amountType"
                    control={control}
                    render={({ field, fieldState: { error } }) => (
                      <Autocomplete
                        {...field}
                        value={
                          deductionAmountTypeOptions.find((op) => field.value === op.value) ?? ""
                        }
                        options={deductionAmountTypeOptions}
                        autoSelect
                        disableClearable
                        onChange={(e, v) => {
                          setValue("amountType", v?.value ?? "flat");
                        }}
                        sx={{ maxHeight: "44px", width: "100%" }}
                        renderInput={(params) => (
                          <CustomTextField
                            type="text"
                            label="Amount Type"
                            showError
                            {...params}
                            InputLabelProps={{ shrink: true }}
                            errorMessage={error?.message}
                          />
                        )}
                      />
                    )}
                  />
                </Grid>
                {/* <Grid item xs={6}>
                  <Controller
                    name="maxAmount"
                    control={control}
                    render={({ field, fieldState: { error } }) => (
                      <CustomTextField
                        {...field}
                        onChange={(e) => {
                          const val = cleanInput(e.target.value ?? "");
                          setValue("maxAmount", val);
                        }}
                        label="Deduction Max"
                        InputLabelProps={{ shrink: true }}
                        showError
                        errorMessage={error?.message}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              {watch("maxAmountType") === "percentage" ? "%" : "$"}
                            </InputAdornment>
                          ),
                        }}
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={6}>
                  <Controller
                    name="maxAmountType"
                    control={control}
                    render={({ field, fieldState: { error } }) => (
                      <Autocomplete
                        {...field}
                        value={
                          deductionAmountTypeOptions.find((op) => field.value === op.value) ?? ""
                        }
                        options={deductionAmountTypeOptions}
                        autoSelect
                        disableClearable
                        onChange={(e, v) => {
                          setValue("maxAmountType", v?.value ?? "flat");
                        }}
                        sx={{ maxHeight: "44px", width: "100%" }}
                        renderInput={(params) => (
                          <CustomTextField
                            type="text"
                            label="Amount Type"
                            showError
                            {...params}
                            InputLabelProps={{ shrink: true }}
                            errorMessage={error?.message}
                          />
                        )}
                      />
                    )}
                  />
                </Grid> */}
                <Grid container item alignItems="center" xs={12}>
                  <Grid item xs={2} alignItems="center">
                    <MDTypography variant="body2" textTransform="capitalize">
                      Sponsor
                    </MDTypography>
                  </Grid>
                  <Grid item xs={4} alignItems="center">
                    <Controller
                      name="sponsor"
                      control={control}
                      render={({ field, fieldState: { error } }) => (
                        <Switch
                          checked={field.value === "Yes"}
                          onChange={(e, v) => {
                            setValue("sponsor", e.target.checked ? "Yes" : "No");
                          }}
                        />
                      )}
                    />
                  </Grid>
                </Grid>
              </Grid>
              <Grid container mt={2} justifyContent="space-between">
                <Grid item>
                  <Button color="error" variant="contained" fullWidth onClick={handleClear}>
                    <MDTypography variant="body2" color="white">
                      Clear
                    </MDTypography>
                  </Button>
                </Grid>
                <Grid container item width="auto" spacing={3} justifyContent="flex-end">
                  <Grid item xs={6}>
                    <Button
                      color="error"
                      variant="contained"
                      fullWidth
                      onClick={async (e) => {
                        e?.stopPropagation();
                        setOpen(false);
                      }}
                    >
                      <MDTypography variant="body2" color="white">
                        Cancel
                      </MDTypography>
                    </Button>
                  </Grid>
                  <Grid item xs={6}>
                    <Button type="submit" variant="contained" fullWidth>
                      <MDTypography variant="body2" color="white">
                        Save
                      </MDTypography>
                    </Button>
                  </Grid>
                </Grid>
              </Grid>
            </form>
          </MDBox>
        </Card>
      </Fade>
    </Modal>
  );
};

export default AddDeductionsModal;
